import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Control, Controller, RegisterOptions } from 'react-hook-form';
import { t } from '@lingui/core/macro';
import cn from 'classnames';

import ArrowForSelect from 'shared/ui/icons/ArrowForSelect';

import styles from './CustomSelect.module.scss';

interface Option {
    value: string | number;
    label: string;
}

interface CustomSelectProps {
    control: Control<any>;
    name: string;
    data: Option[];
    placeholder?: string;
    defaultValue?: string | number;
    rules?: RegisterOptions;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
    control,
    name,
    data,
    placeholder,
    defaultValue,
    rules,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef<HTMLDivElement>(null);
    const optionsRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

    const updatePosition = () => {
        if (selectRef.current) {
            const rect = selectRef.current.getBoundingClientRect();
            setPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width,
            });
        }
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (selectRef.current && !selectRef.current.contains(event.target as Node) &&
            optionsRef.current && !optionsRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (isOpen) {
            updatePosition();
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition, true);
        } else {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        }

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen]);

    return (
        <Controller
            control={control}
            name={name}
            defaultValue={defaultValue}
            rules={rules}
            render={({ field: { onChange, value } }) => {
                const selectedLabel = data.find(opt => opt.value === value)?.label || placeholder || t`Выберите...`;

                return (
                    <div className={styles.customSelect} ref={selectRef}>
                        <div
                            className={cn(
                                styles.selectHeader,
                                isOpen ? styles.open : '',
                                styles.colorHeader,
                            )}
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsOpen(!isOpen);
                                if (!isOpen) updatePosition();
                            }}
                        >
                            <span className={styles.selectedValue}>
                                {selectedLabel}
                            </span>
                            <div className={cn(
                                styles.arrow,
                                isOpen ? styles.rotated : '',
                            )}>
                                <ArrowForSelect />
                            </div>
                        </div>

                        {isOpen && createPortal(
                            <div
                                className={cn(
                                    styles.optionsList,
                                    styles.colorsList,
                                )}
                                ref={optionsRef}
                                style={{
                                    top: `${position.top}px`,
                                    left: `${position.left}px`,
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {data.map(option => (
                                    <div
                                        key={option.value}
                                        className={styles.option}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onChange(option.value);
                                            setIsOpen(false);
                                        }}
                                    >
                                        {option.label}
                                    </div>
                                ))}
                            </div>,
                            document.body,
                        )}
                    </div>
                );
            }}
        />
    );
};

export default CustomSelect;